import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Overlay, Table, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import axios from 'helpers/api';

// Hourly fields come back as "06AM-07AM" … "11PM-12AM" (18 of them).
const HOUR_KEY = /^(\d{2})(AM|PM)-(\d{2})(AM|PM)$/;

const TOTALS = [
  { key: 'Total PV2 Rx', label: 'Total Rx' },
  { key: 'Total PV2 Unit-Dose', label: 'Unit-Dose' },
  { key: 'Total PV2 Multidose', label: 'Multidose' }
];

// Keeps the three total columns narrow so the hours get the room.
const TOTAL_COLUMN_WIDTH = '76px';

// Heat levels for the hourly cells: busier hours get a stronger tint.
const HEAT_CLASSES = [
  'text-400',
  'bg-info bg-opacity-10 text-900',
  'bg-info bg-opacity-25 text-900',
  'bg-info bg-opacity-50 text-900',
  'bg-info bg-opacity-75 text-900 fw-semibold'
];

const toNumber = value => Number(value) || 0;
const formatNumber = value => value.toLocaleString('en-US');

const formatLastSyncedAt = value => {
  if (!value) return null;
  const parsed = moment(value, 'YYYY-MM-DD HH:mm:ss', true);
  return parsed.isValid() ? parsed.format('MM/DD/YY, HH:mm') : value;
};

// "06AM-07AM" → { hour: 6, half: 'AM', label: '6', range: '6–7 AM', order: 6 }
const parseHourKey = key => {
  const [, from, fromHalf, to, toHalf] = key.match(HOUR_KEY);
  const hour = Number(from);
  const end = Number(to);
  const to24 = (h, half) => (h % 12) + (half === 'PM' ? 12 : 0);
  return {
    key,
    hour,
    half: fromHalf,
    label: String(hour),
    range:
      fromHalf === toHalf
        ? `${hour}–${end} ${toHalf}`
        : `${hour} ${fromHalf}–${end} ${toHalf}`,
    order: to24(hour, fromHalf)
  };
};

const share = (value, total) =>
  total > 0 ? `${Math.round((value / total) * 100)}% of the day` : '';

const heatLevel = (value, max) =>
  value > 0 && max > 0 ? Math.min(4, Math.ceil((value / max) * 4)) : 0;

const Stat = ({ label, value, sub }) => (
  <div className="border border-200 rounded-3 px-3 py-2">
    <div className="fs-11 text-500">{label}</div>
    <div className="fs-8 fw-bold text-900 lh-sm">
      {value}
      {sub && <span className="fs-11 fw-normal text-500 ms-1">{sub}</span>}
    </div>
  </div>
);

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  sub: PropTypes.node
};

const RxVerifiedPackTypeWidget = () => {
  const [date, setDate] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // One tooltip for the whole grid, moved to whichever cell is hovered.
  const [tip, setTip] = useState(null);

  const loadData = useCallback(async selectedDate => {
    setIsLoading(true);
    try {
      const { data: body } = await axios.get(
        '/dashboard/rx-verified-packtype',
        { params: { date: moment(selectedDate).format('YYYY-MM-DD') } }
      );
      setRows(Array.isArray(body?.data) ? body.data : []);
      setLastSyncedAt(body?.last_synced_at || null);
      setError('');
    } catch (requestError) {
      setRows([]);
      setLastSyncedAt(null);
      setError('Unable to load RX verified pack type. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(date);
  }, [date, loadData]);

  const view = useMemo(() => {
    const hours = [
      ...new Set(
        rows.flatMap(row => Object.keys(row).filter(key => HOUR_KEY.test(key)))
      )
    ]
      .map(parseHourKey)
      .sort((a, b) => a.order - b.order);

    // Busiest verifier first.
    const users = rows
      .map(row => ({
        user: row.USER || '—',
        totals: TOTALS.map(({ key }) => toNumber(row[key])),
        hourly: hours.map(({ key }) => toNumber(row[key]))
      }))
      .sort(
        (a, b) => b.totals[0] - a.totals[0] || a.user.localeCompare(b.user)
      );

    const sum = values => values.reduce((total, value) => total + value, 0);
    const totals = TOTALS.map((_, i) => sum(users.map(u => u.totals[i])));
    const hourlyTotals = hours.map((_, i) => sum(users.map(u => u.hourly[i])));
    const max = Math.max(0, ...users.flatMap(u => u.hourly));
    const busiest = hourlyTotals.reduce(
      (best, value, i) => (value > (best?.value ?? 0) ? { value, i } : best),
      null
    );

    return {
      hours,
      users,
      totals,
      hourlyTotals,
      max,
      busiest: busiest && { ...hours[busiest.i], value: busiest.value },
      halves: ['AM', 'PM']
        .map(half => ({
          half,
          span: hours.filter(hour => hour.half === half).length
        }))
        .filter(({ span }) => span > 0)
    };
  }, [rows]);

  const showTip = event => {
    const cell = event.target.closest('[data-tip]');
    if (!cell) {
      setTip(current => current && { ...current, show: false });
      return;
    }
    setTip(current =>
      current?.target === cell && current.show
        ? current
        : {
            target: cell,
            title: cell.dataset.tip,
            detail: cell.dataset.tipDetail,
            note: cell.dataset.tipNote,
            show: true
          }
    );
  };

  const hideTip = () =>
    setTip(current => current && { ...current, show: false });

  const syncedAt = formatLastSyncedAt(lastSyncedAt);
  const dayLabel = moment(date).format('MM/DD/YY');

  return (
    <Card className="h-100 overflow-hidden shadow-sm">
      <Card.Header className="pb-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
        <h6 className="mb-0 text-900">RX Verified Pack Type</h6>
        <div>
          <DatePicker
            selected={date}
            onChange={value => value && setDate(value)}
            maxDate={new Date()}
            className="form-control form-control-sm w-auto"
            dateFormat="MM/dd/yy"
          />
        </div>
      </Card.Header>
      <Card.Body className="pt-3">
        {isLoading ? (
          <div className="py-4 text-center text-500">
            <FontAwesomeIcon icon="spinner" spin className="me-2" />
            Loading RX verified pack type...
          </div>
        ) : error ? (
          <div className="py-4 text-center text-danger">{error}</div>
        ) : view.users.length === 0 ? (
          <div className="py-4 text-center text-500 bg-100 rounded-2">
            No data
          </div>
        ) : (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Stat
                label={TOTALS[0].label}
                value={formatNumber(view.totals[0])}
              />
              <Stat
                label={TOTALS[1].label}
                value={formatNumber(view.totals[1])}
              />
              <Stat
                label={TOTALS[2].label}
                value={formatNumber(view.totals[2])}
              />
              <Stat label="Users" value={view.users.length} />
              {view.busiest && (
                <Stat
                  label="Busiest hour"
                  value={view.busiest.range}
                  sub={`${formatNumber(view.busiest.value)} Rx`}
                />
              )}
            </div>

            <div className="table-responsive scrollbar">
              <Table
                size="sm"
                bordered
                hover
                className="fs-10 mb-0 text-center"
                onMouseOver={showTip}
                onMouseLeave={hideTip}
              >
                <caption className="visually-hidden">
                  RX verified per user and hour on {dayLabel}
                </caption>
                <thead className="bg-200 text-900 align-middle">
                  <tr>
                    <th scope="col" rowSpan={2} className="text-start">
                      User
                    </th>
                    {TOTALS.map(({ key, label }) => (
                      <th
                        key={key}
                        scope="col"
                        rowSpan={2}
                        style={{ width: TOTAL_COLUMN_WIDTH }}
                      >
                        {label}
                      </th>
                    ))}
                    {view.halves.map(({ half, span }) => (
                      <th
                        key={half}
                        scope="colgroup"
                        colSpan={span}
                        className="py-1 fs-11"
                      >
                        {half}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {view.hours.map((hour, i) => (
                      <th
                        key={hour.key}
                        scope="col"
                        data-tip={hour.range}
                        data-tip-detail={`${formatNumber(
                          view.hourlyTotals[i]
                        )} Rx verified by all users`}
                        className="px-1 py-1 fs-11 fw-semibold"
                      >
                        {hour.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="align-middle">
                  {view.users.map((row, rowIndex) => (
                    <tr key={`${row.user}-${rowIndex}`}>
                      <th scope="row" className="text-start text-900">
                        {row.user}
                      </th>
                      {row.totals.map((value, i) => (
                        <td
                          key={TOTALS[i].key}
                          className={i === 0 ? 'fw-bold text-900' : ''}
                        >
                          {formatNumber(value)}
                        </td>
                      ))}
                      {row.hourly.map((value, i) => (
                        <td
                          key={view.hours[i].key}
                          data-tip={`${row.user} · ${view.hours[i].range}`}
                          data-tip-detail={`${formatNumber(value)} Rx verified`}
                          data-tip-note={share(value, row.totals[0])}
                          className={`px-1 ${
                            HEAT_CLASSES[heatLevel(value, view.max)]
                          }`}
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-100 fw-bold text-900 align-middle">
                  <tr>
                    <th scope="row" className="text-start">
                      Total
                    </th>
                    {view.totals.map((value, i) => (
                      <td key={TOTALS[i].key}>{formatNumber(value)}</td>
                    ))}
                    {view.hourlyTotals.map((value, i) => (
                      <td
                        key={view.hours[i].key}
                        data-tip={`All users · ${view.hours[i].range}`}
                        data-tip-detail={`${formatNumber(value)} Rx verified`}
                        data-tip-note={share(value, view.totals[0])}
                        className="px-1"
                      >
                        {formatNumber(value)}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </Table>
            </div>
            <Overlay
              target={tip?.target}
              show={Boolean(tip?.show)}
              placement="top"
            >
              {props => (
                <Tooltip id="rx-verified-pack-type-tip" {...props}>
                  <div className="fw-semibold">{tip?.title}</div>
                  <div>{tip?.detail}</div>
                  {tip?.note && <div className="opacity-75">{tip.note}</div>}
                </Tooltip>
              )}
            </Overlay>
          </>
        )}

        {!isLoading && !error && (
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 fs-11 text-500">
            <span>Last synced: {syncedAt || '—'}</span>
            {view.users.length > 0 && (
              <span className="d-inline-flex align-items-center gap-1">
                Fewer
                {HEAT_CLASSES.slice(1).map(heat => (
                  <span
                    key={heat}
                    className={`d-inline-block border border-200 rounded-1 px-2 py-1 ${heat}`}
                    aria-hidden="true"
                  />
                ))}
                More Rx per hour
              </span>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default RxVerifiedPackTypeWidget;
